import { useState } from "react";
import { Lightbulb, Star, Check, X, Thermometer, CalendarDays } from "lucide-react";

const P = ({ b }) => <p className="text-[15px] sm:text-base text-[#3F3833] leading-relaxed">{b.text}</p>;
const H = ({ b }) => <h3 className="font-serif text-2xl text-[#2D2825] pt-4">{b.text}</h3>;
const Tip = ({ b }) => (
  <div className="flex gap-3 rounded-2xl bg-[#FDF6E7] border border-[#F2D293] p-4">
    <Lightbulb size={20} className="text-[#C29638] shrink-0 mt-0.5" />
    <p className="text-sm text-[#6b5320] leading-relaxed">{b.text}</p>
  </div>
);
const List = ({ items }) => (
  <ul className="space-y-2">
    {items.map((t) => (
      <li key={t} className="flex gap-2.5 text-[15px] text-[#3F3833] leading-relaxed">
        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#C87D75] shrink-0" />{t}
      </li>
    ))}
  </ul>
);

const Sizes = ({ sizes, highlight }) => (
  <div className="rounded-2xl border border-[#EAE3DC] bg-white overflow-hidden" data-testid="lesson-size-table">
    <div className="grid grid-cols-[1.1fr_1fr_1fr] sm:grid-cols-[1fr_1fr_1fr_1.4fr] text-[11px] uppercase tracking-widest text-[#8C7F78] bg-[#FAF8F5] px-4 py-2.5">
      <span>Taglia</span><span>Lunghezza</span><span>Peso</span><span className="hidden sm:block">Età indicativa</span>
    </div>
    {sizes.map((r) => (
      <div key={r.size} className={`grid grid-cols-[1.1fr_1fr_1fr] sm:grid-cols-[1fr_1fr_1fr_1.4fr] px-4 py-3 border-t border-[#F4EFEA] text-sm ${r.size === highlight ? "bg-[#F9ECE9]" : ""}`}>
        <span className="font-medium text-[#2D2825]">{r.size}{r.size === highlight && <span className="block text-[10px] text-[#C87D75] font-normal">la tua taglia nascita</span>}</span>
        <span className="text-[#5C534E]">{r.cm}</span><span className="text-[#5C534E]">{r.kg}</span>
        <span className="hidden sm:block text-[#8C7F78]">{r.age}</span>
        <span className="col-span-3 sm:col-span-4 text-xs text-[#8C7F78] mt-1">{r.note}</span>
      </div>
    ))}
  </div>
);

const it = (n) => String(n).replace(".", ",");

const SizePlan = ({ plan }) => (
  <div className="rounded-2xl bg-white border border-[#E5A8A0] p-5" data-testid="lesson-size-plan">
    {plan.born ? (
      <p className="text-[15px] text-[#3F3833]">
        {plan.measured_month != null
          ? <>Dalle ultime misure ({it(plan.weight)} kg, {it(plan.length)} cm) il tuo bimbo oggi veste la </>
          : <>In base all'età, oggi il tuo bimbo veste circa la </>}
        <strong className="text-[#C87D75]">{plan.current_size}</strong>.
        {plan.measured_month == null && " Aggiungi peso e lunghezza nel profilo per un calcolo su misura."} Ecco le prossime taglie:
      </p>
    ) : (
      <p className="text-[15px] text-[#3F3833]">
        Il tuo bimbo è previsto di circa <strong>{it(plan.weight)} kg</strong> e <strong>{it(plan.length)} cm</strong>
        {plan.estimated && " (valori medi: aggiornali nel profilo per un calcolo ancora più preciso)"}.
        La taglia giusta per la nascita è la <strong className="text-[#C87D75]">{plan.birth_size}</strong>. Ecco quando cambierà taglia:
      </p>
    )}
    <ol className="mt-4 relative border-l-2 border-[#F0DDD3] ml-2 space-y-4">
      {plan.plan.map((s, i) => (
        <li key={s.size} className="pl-5 relative" data-testid={`size-plan-${i}`}>
          <span className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white ${i === 0 ? "bg-[#C87D75]" : "bg-[#D9C9BC]"}`} />
          <div className="font-medium text-[#2D2825]">{s.size} <span className="text-xs text-[#8C7F78] font-normal">· {s.cm}</span></div>
          <div className="text-sm text-[#5C534E] flex items-center gap-1.5"><CalendarDays size={13} /> da {s.from} a {s.to}</div>
        </li>
      ))}
    </ol>
  </div>
);

const Climate = ({ c }) => (
  <div className="rounded-2xl bg-white border border-[#CFE0D6] p-5" data-testid="lesson-climate">
    <div className="flex items-center gap-3">
      <Thermometer className="text-[#4E7363]" />
      <div className="text-[15px] text-[#3F3833]">A <strong>{c.city}</strong> a <strong>{c.month_name}</strong> (stagione {c.season}) la temperatura media è di
        circa <strong>{c.avg}°C</strong>, tra {c.min}° e {c.max}°.</div>
    </div>
    <div className="mt-3 text-xs uppercase tracking-widest text-[#8C7F78]">Cosa fargli indossare</div>
    <div className="mt-2"><List items={c.clothing || []} /></div>
  </div>
);

const Fabric = ({ f }) => (
  <div className={`rounded-2xl border p-5 bg-white ${f.star ? "border-[#E5A8A0] ring-2 ring-[#F9ECE9]" : f.avoid ? "border-[#E8C9C5] bg-[#FDF7F6]" : "border-[#EAE3DC]"}`} data-testid="lesson-fabric">
    <div className="flex items-center justify-between gap-2">
      <div className="font-serif text-xl text-[#2D2825] flex items-center gap-2">{f.star && <Star size={16} className="text-[#C29638] fill-[#F2D293]" />}{f.name}</div>
      <span className="text-[11px] rounded-full bg-[#F4EFEA] text-[#5C534E] px-2.5 py-0.5">{f.seasons}</span>
    </div>
    <p className="mt-2 text-sm text-[#3F3833] leading-relaxed">{f.what}</p>
    <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
      <div>{f.pro.map((t) => <div key={t} className="flex gap-1.5 text-[#3F5E50]"><Check size={14} className="mt-0.5 shrink-0" />{t}</div>)}</div>
      <div>{f.contro.map((t) => <div key={t} className="flex gap-1.5 text-[#9A5A52]"><X size={14} className="mt-0.5 shrink-0" />{t}</div>)}</div>
    </div>
    <div className="mt-3 text-xs text-[#5C534E]"><strong>Quando:</strong> {f.when}</div>
    {f.care !== "-" && <div className="text-xs text-[#5C534E]"><strong>Lavaggio:</strong> {f.care}</div>}
  </div>
);

const FabricGame = ({ game }) => {
  const [ans, setAns] = useState({});
  return (
    <div className="space-y-4" data-testid="lesson-fabric-game">
      {game.map((g, i) => (
        <div key={g.q} className="rounded-2xl bg-white border border-[#EAE3DC] p-5">
          <div className="font-medium text-[#2D2825]">{g.q}</div>
          <div className="mt-3 grid sm:grid-cols-3 gap-2">
            {g.options.map((o, j) => {
              const picked = ans[i] === j; const show = ans[i] !== undefined;
              return (
                <button key={o} onClick={() => setAns((a) => ({ ...a, [i]: j }))} data-testid={`fabric-game-${i}-${j}`}
                  className={`rounded-xl border px-3 min-h-[44px] text-sm transition-colors ${show && j === g.correct ? "bg-[#EAF0EC] border-[#4E7363] text-[#2F4B3F]" : picked ? "bg-[#FBEDEB] border-[#C87D75]" : "border-[#EAE3DC] hover:border-[#E5A8A0]"}`}>{o}</button>
              );
            })}
          </div>
          {ans[i] !== undefined && <p className={`mt-2 text-sm ${ans[i] === g.correct ? "text-[#4E7363]" : "text-[#9A5A52]"}`}>{ans[i] === g.correct ? "Esatto! " : "Quasi! "}{g.explain}</p>}
        </div>
      ))}
    </div>
  );
};

export const LessonBlocks = ({ blocks, data }) => (
  <div className="space-y-5">
    {blocks.map((b, i) => {
      switch (b.t) {
        case "p": return <P key={`${b.t}-${i}`} b={b} />;
        case "h": return <H key={`${b.t}-${i}`} b={b} />;
        case "tip": return <Tip key={`${b.t}-${i}`} b={b} />;
        case "list": return <List key={`${b.t}-${i}`} items={b.items} />;
        case "sizes": return <Sizes key={`${b.t}-${i}`} sizes={data.sizes} highlight={data.size_plan?.birth_size} />;
        case "size_plan": return data.size_plan ? <SizePlan key={`${b.t}-${i}`} plan={data.size_plan} /> : null;
        case "climate": return data.climate ? <Climate key={`${b.t}-${i}`} c={data.climate} /> : null;
        case "fabrics": return <div key={`${b.t}-${i}`} className="grid gap-4">{data.fabrics.map((f) => <Fabric key={f.name} f={f} />)}</div>;
        case "fabric_game": return <FabricGame key={`${b.t}-${i}`} game={data.fabric_game} />;
        case "bag_list": return <List key={`${b.t}-${i}`} items={data.bag_list} />;
        case "growth": return data.size_plan ? <SizePlan key={`${b.t}-${i}`} plan={data.size_plan} /> : null;
        default: return null;
      }
    })}
  </div>
);
